import React, { useEffect, useRef, useState } from 'react';

import axiosInstance from '../../services/axiosInstance';
import { eventService } from '../../services/eventService';
import { BillingModal } from '../BillingModal';

import './AgentProgressTracker.css';
import { useDocProcessing } from './useDocProcessing';

const STATUS_LABELS: Record<string, string> = {
  parsing: 'Reading the document…',
  processing: 'Finding requirements…',
  chunking: 'Organizing sections…',
  embedding: 'Preparing document content…',
  indexing: 'Saving document content…',
  indexing_complete: 'Document content saved',
  extracting_requirements: 'Extracting document requirements…',
  preparing: 'Preparing document requirements…',
  waiting_credits: 'Processing paused because there aren’t enough credits.',
  updating_srd: 'Updating requirements…',
  reconciling: 'Finalizing requirements…',
  complete: 'Requirements extracted and saved. Click Start to create the SRD.',
  completed: 'Requirements extracted and saved. Click Start to create the SRD.',
  error: 'We couldn’t finish processing this document.',
};

const CheckIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PlanningIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const FileIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v5h5" />
    <path d="M9 13h6" />
    <path d="M9 17h4" />
  </svg>
);

type BillingView = 'credits' | 'plans';
type DocumentState = NonNullable<ReturnType<typeof useDocProcessing>>;

const DocumentCreditActions: React.FC<{ onOpenBilling: (view: BillingView) => void }> = ({ onOpenBilling }) => (
  <>
    <button className="app-action-btn app-action-btn--warning" onClick={() => onOpenBilling('credits')} type="button">
      Add Credits
    </button>
    <button className="app-action-btn app-action-btn--upgrade" onClick={() => onOpenBilling('plans')} type="button">
      Upgrade
    </button>
  </>
);

// eslint-disable-next-line max-lines-per-function -- Keep the shared request guard with both recovery actions.
const DocumentRecoveryActions: React.FC<{ state: DocumentState }> = ({ state }) => {
  const [busy, setBusy] = useState<'retry' | 'exclude' | null>(null);
  const [error, setError] = useState('');
  const request = useRef({ inFlight: false, mounted: false, state });

  useEffect(() => {
    const current = request.current;

    current.state = state;
    current.mounted = true;

    return () => { current.mounted = false; };
  }, [state]);
  // eslint-disable-next-line max-statements -- Keep request guards and UI transitions together.
  const recover = async (action: 'retry' | 'exclude') => {
    if (request.current.inFlight) return;
    request.current.inFlight = true;
    setBusy(action);
    setError('');
    try {
      const { data } = await axiosInstance.post(
        `/api/v1/projects/${state.projectId}/documents/${state.documentId}/${action}`,
      );

      if (request.current.mounted && request.current.state === state) {
        if (action === 'exclude') eventService.emit('doc_processing', {
          project_id: state.projectId, document_id: state.documentId, status: 'excluded',
        });
        if (data.processing || action === 'retry') eventService.emit('doc_processing', data.processing ?? {
          project_id: state.projectId, status: 'completed', batch_complete: true,
        });
      }
    } catch {
      if (request.current.mounted) setError(action === 'retry'
        ? 'Could not request a retry. Please try again.' : 'Could not exclude the document. Please try again.');
    } finally {
      request.current.inFlight = false;
      if (request.current.mounted) setBusy(null);
    }
  };

  return (
    <>
      {state.canRetry && <button className="app-action-btn app-action-btn--primary" type="button" disabled={!!busy} onClick={() => { void recover('retry'); }}>
        {busy === 'retry' ? 'Retrying…' : 'Retry'}
      </button>}
      <button className="app-action-btn" type="button" disabled={!!busy} onClick={() => { void recover('exclude'); }}>
        {busy === 'exclude' ? 'Excluding…' : 'Exclude document'}
      </button>
      {error && <span role="alert">{error}</span>}
    </>
  );
};

interface DocumentProgressCardProps {
  state: DocumentState;
  onOpenBilling: (view: BillingView) => void;
}

// eslint-disable-next-line max-lines-per-function -- The progress card renders one document state.
const DocumentProgressCard: React.FC<DocumentProgressCardProps> = ({ state, onOpenBilling }) => {
  const isDone = state.status === 'completed' || state.status === 'complete';
  const isError = state.status === 'error';
  const needsCredits = state.status === 'waiting_credits';
  const label = state.status === 'extracting_requirements' && state.totalBatches
    ? `Extracting requirements — ${state.completedBatches ?? 0} of ${state.totalBatches} sections complete`
    : state.errorMessage || STATUS_LABELS[state.status] || state.status;
  const pct = isDone ? 100 : Math.max(0, Math.min(state.progress, 100));
  const creditSuffix =
    isDone && state.creditsDeducted !== undefined && state.creditsDeducted > 0
      ? ` · −${state.creditsDeducted.toFixed(2)} cr`
      : '';

  return (
    <div className={`doc-processing-card${isDone ? ' doc-processing-card--done' : ''}${isError ? ' doc-processing-card--error' : ''}${needsCredits ? ' doc-processing-card--paused' : ''}`}>
      <div className="doc-processing-card__header">
        <div className="doc-processing-card__title-group">
          <span className="doc-processing-card__file-icon" aria-hidden="true">
            {isDone ? <CheckIcon /> : needsCredits ? <PlanningIcon /> : <FileIcon />}
          </span>
          <div className="doc-processing-card__summary">
            <div className="doc-processing-card__title">
              {needsCredits ? 'Add credits to continue' : isError ? 'Document processing paused' : isDone ? 'Document ready' : 'Analyzing your document'}
            </div>
            <div className="doc-processing-card__filename" title={state.filename}>
              {state.filename}
            </div>
          </div>
        </div>
        <span className="doc-processing-card__status">
          {isDone
            ? `Done${creditSuffix}`
            : isError
              ? 'Needs attention'
              : needsCredits ? 'Paused' : `${pct}%`}
        </span>
      </div>

      <div className="doc-processing-card__body">
        <div className="doc-processing-card__phase" role="status" aria-live="polite" aria-atomic="true">
          {label}
        </div>

        <div className="doc-processing-card__track" role="progressbar" aria-label="Document processing"
          aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-valuetext={label}>
          <div className="doc-processing-card__fill" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {(needsCredits || isError) && (
        <div className="doc-processing-card__actions">
          {needsCredits && <DocumentCreditActions onOpenBilling={onOpenBilling} />}
          {state.documentId && (
            <DocumentRecoveryActions key={`${state.projectId}:${state.documentId}`} state={state} />
          )}
        </div>
      )}
      {isError && <p className="doc-processing-card__hint">
        {state.canRetry
          ? 'Your document and completed work are saved. Retry to continue without uploading again.'
          : 'Your upload is saved. Exclude it to continue with other documents.'}
        {state.totalBatches ? ` ${state.completedBatches ?? 0} of ${state.totalBatches} sections complete.` : ''}
      </p>}
      {needsCredits && (
        <p className="doc-processing-card__hint">
          Your document and completed work are saved. Add credits or upgrade, then click Retry to continue.
        </p>
      )}
    </div>
  );
};

/** Keep billing open even when document progress changes in another tab. */
export const DocProcessingBar: React.FC<{ projectId?: string | null }> = ({ projectId }) => {
  const state = useDocProcessing(projectId);
  const [billingView, setBillingView] = useState<BillingView | null>(null);

  return (
    <>
      {state && <DocumentProgressCard state={state} onOpenBilling={setBillingView} />}
      {billingView && <BillingModal initialView={billingView} onClose={() => setBillingView(null)} />}
    </>
  );
};
